/**
 * Beschreibung einer Animationsvorlage.
 *
 * Eine Vorlage ist eine reine Funktion: Aus Parametern und einem Zeitpunkt
 * entsteht eine Szene. Dadurch sind die Bilder berechenbar und lassen sich
 * in Tests und im Prüfskript vergleichen.
 */
import type { Scene } from './scene.ts';

/** Bereiche der Vorlagen, in der Reihenfolge der Galerie. */
export const TEMPLATE_GROUPS = {
  cut: 'Schneiden',
  mix: 'Mischen',
  stove: 'Herd',
  bake: 'Backen',
  prep: 'Vorbereiten & Anrichten',
} as const;

/** Bereich einer Vorlage. */
export type TemplateGroup = keyof typeof TEMPLATE_GROUPS;

/** Erlaubter Wert eines Parameters. */
export type ParameterValue = string | number | boolean;

/** Werte einer Vorlage. */
export type TemplateValues = Readonly<Record<string, ParameterValue>>;

/** Auswahlparameter. */
export interface ChoiceParameter {
  readonly kind: 'choice';
  readonly name: string;
  readonly label: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly defaultValue: string;
}

/** Zahlenparameter. */
export interface NumberParameter {
  readonly kind: 'number';
  readonly name: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  /** Einheit für die Anzeige, zum Beispiel "°C" oder "g". */
  readonly unit?: string;
  readonly defaultValue: number;
}

/** Ja/Nein-Parameter. */
export interface BooleanParameter {
  readonly kind: 'boolean';
  readonly name: string;
  readonly label: string;
  readonly defaultValue: boolean;
}

/** Ein Parameter einer Vorlage. */
export type TemplateParameter = ChoiceParameter | NumberParameter | BooleanParameter;

/** Eine Vorlage. */
export interface AnimationTemplate {
  readonly id: string;
  readonly group: TemplateGroup;
  readonly title: string;
  /** Beschreibung für Screenreader und Tooltips. */
  readonly description: string;
  /** Dauer einer Schleife in Sekunden. */
  readonly durationSeconds: number;
  /** Stichwörter (Wortanfänge) für Vorschläge aus dem Schritttext. */
  readonly keywords: readonly string[];
  readonly parameters: readonly TemplateParameter[];
  /** Zeitpunkt des aussagekräftigsten Standbilds, 0 bis 1. */
  readonly stillTime: number;
  /**
   * Baut die Szene.
   * @param values Vollständige Werte (siehe withDefaults).
   * @param time Zeitpunkt innerhalb der Schleife, 0 bis 1.
   */
  readonly scene: (values: TemplateValues, time: number) => Scene;
  /**
   * Richtungspfeile und Hinweise für das Standbild bei reduzierter Bewegung.
   * Sie werden über das Standbild gelegt.
   */
  readonly hints?: (values: TemplateValues) => Scene;
  /**
   * Liest passende Werte aus dem Schritttext, zum Beispiel "180 Grad".
   * Nicht erkannte Werte dürfen fehlen oder undefined sein.
   */
  readonly inferValues?: (text: string) => Readonly<Record<string, ParameterValue | undefined>>;
}

/** Setzt fehlende oder ungültige Werte auf die Vorgaben der Vorlage. */
export function withDefaults(
  template: AnimationTemplate,
  values: Readonly<Record<string, unknown>> = {},
): TemplateValues {
  const result: Record<string, ParameterValue> = {};
  for (const parameter of template.parameters) {
    result[parameter.name] = sanitize(parameter, values[parameter.name]);
  }
  return result;
}

/** Prüft einen einzelnen Wert gegen seinen Parameter. */
function sanitize(parameter: TemplateParameter, value: unknown): ParameterValue {
  switch (parameter.kind) {
    case 'choice':
      return typeof value === 'string' && parameter.options.some((option) => option.value === value)
        ? value
        : parameter.defaultValue;
    case 'number': {
      const number = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
      return Number.isFinite(number)
        ? Math.min(parameter.max, Math.max(parameter.min, number))
        : parameter.defaultValue;
    }
    case 'boolean':
      return typeof value === 'boolean' ? value : parameter.defaultValue;
    default:
      return '';
  }
}

/** Liest einen Textwert. */
export function textValue(values: TemplateValues, name: string, fallback: string): string {
  const value = values[name];
  return typeof value === 'string' ? value : fallback;
}

/** Liest einen Zahlenwert. */
export function numberValue(values: TemplateValues, name: string, fallback: number): number {
  const value = values[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Liest einen Wahrheitswert. */
export function booleanValue(values: TemplateValues, name: string, fallback: boolean): boolean {
  const value = values[name];
  return typeof value === 'boolean' ? value : fallback;
}

/** Hilfe zum Anlegen eines Auswahlparameters. */
export function choice(
  name: string,
  label: string,
  options: Readonly<Record<string, string>>,
  defaultValue: string,
): ChoiceParameter {
  return {
    kind: 'choice',
    name,
    label,
    defaultValue,
    options: Object.entries(options).map(([value, text]) => ({ value, label: text })),
  };
}

/** Hilfe zum Anlegen einer Vorlage mit Prüfung der Grundangaben. */
export function defineTemplate(template: AnimationTemplate): AnimationTemplate {
  return template;
}
